import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import CommodityOptions from "@/components/CommodityOptions";
import LocationFields from "@/components/LocationFields";
import { t } from "@/lib/i18n";

const INIT = { commodity: "", tonnage: "", loading_date: "", offloading_date: "", price_per_mt: "", loading_address: "",
  loading_area: "", loading_region: "", loading_country: "", offloading_address: "", offloading_area: "", offloading_region: "",
  offloading_country: "", truck_type: "", notes: "" };

const Field = ({ label, children }) => <div><label className="hf-label">{label}</label>{children}</div>;

export default function PostLoad() {
  const { meta, user, refresh } = useAuth();
  const navigate = useNavigate();
  const repost = useLocation().state?.repost;
  const [f, setF] = useState(() => {
    const base = repost ? Object.fromEntries(Object.keys(INIT).map((k) => [k, k.endsWith("_date") ? "" : String(repost[k] ?? "")])) : INIT;
    return { ...base, loading_country: base.loading_country || user.country || "", offloading_country: base.offloading_country || user.country || "" };
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const inp = (k, type = "text", extra = {}) => <input type={type} required value={f[k]} onChange={set(k)} className="hf-input" data-testid={`post-load-${k}-input`} {...extra} />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/loads", { ...f, tonnage: Number(f.tonnage), price_per_mt: Number(f.price_per_mt) });
      toast.success(t("Load posted — 1 credit used"));
      refresh();
      navigate(`/loads/${data.id}`);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  const blocked = user.status !== "approved" || user.credits < 1;
  const total = Number(f.tonnage) * Number(f.price_per_mt);

  return (
    <div className="max-w-4xl">
      <PageHeader kicker={t("New listing · costs 1 credit")} title={repost ? t("Repost load") : t("Post a load")} />
      {repost && <div className="hf-card p-4 mb-6 border-blue-200 bg-blue-50 text-sm text-blue-900" data-testid="repost-banner">{t("Details copied from your previous load. Pick new start and completion dates, then publish.")}</div>}
      {blocked && (
        <div className="hf-card p-4 mb-6 border-amber-300 bg-amber-50 text-sm" data-testid="post-load-blocked">
          {user.status !== "approved" ? t("Your account must be approved by an admin before posting.") : <>{t("You have 0 credits.")} <Link to="/wallet" className="font-semibold underline">{t("Buy credits")}</Link> {t("to post.")}</>}
        </div>
      )}
      <form onSubmit={submit} className="space-y-6" data-testid="post-load-form">
        <div className="hf-card p-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label={t("Commodity")}>
            <select required value={f.commodity} onChange={set("commodity")} className="hf-input" data-testid="post-load-commodity-select">
              <option value="">{t("Select…")}</option><CommodityOptions meta={meta} />
            </select>
          </Field>
          <Field label={t("Tonnage (t)")}>{inp("tonnage", "number", { min: "1", step: "0.1" })}</Field>
          <Field label={`${t("Price per MT")} (${user.currency || "USD"})`}>{inp("price_per_mt", "number", { min: "0.01", step: "0.01" })}</Field>
          <Field label={t("Truck type")}>
            <select required value={f.truck_type} onChange={set("truck_type")} className="hf-input" data-testid="post-load-truck-type-select">
              <option value="">{t("Select…")}</option>{meta.truck_types.map((c) => <option key={c} value={c}>{t(c)}</option>)}
            </select>
          </Field>
          <Field label={t("Start date")}>{inp("loading_date", "date")}</Field>
          <Field label={t("Completion date")}>{inp("offloading_date", "date", { min: f.loading_date })}</Field>
          <div className="sm:col-span-2 flex items-end">
            <p className="font-mono text-sm text-slate-600" data-testid="post-load-total">{t("EST. TOTAL:")} <span className="font-bold text-slate-900">{total ? money(total, user.currency || "USD") : "—"}</span></p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {["loading", "offloading"].map((k) => <LocationFields key={k} kind={k} f={f} setF={setF} meta={meta} inp={inp} />)}
        </div>
        <div className="hf-card p-6">
          <Field label={t("Notes (optional)")}>
            <textarea rows={3} value={f.notes} onChange={set("notes")} className="hf-input" data-testid="post-load-notes-input" placeholder={t("Loading hours, weighbridge, tarp requirements…")} />
          </Field>
        </div>
        <button disabled={busy || blocked} className="hf-btn-primary h-12 px-8" data-testid="post-load-submit-btn">{busy ? t("Posting…") : t("Publish load · 1 credit")}</button>
      </form>
    </div>
  );
}
